import * as uuid from "uuid";
import socketIOClient from "socket.io-client";
import { AnyAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { action } from "typesafe-actions";

import api from "../../../api";
import { APIResponse } from "../../../types/APIResponseType";
import {
  AdGroup,
  AdGroupCreationBody,
  AdGroupTemplate,
  AdGroupTemplateCreationBody,
  AdGroupType,
  CreativeAsset,
  CreativeAssetsByAdGroup,
} from "../../../types/adGroupTypes";
import {
  AdGroupToCreateType,
  CampaignBasicDetailsType,
  CampaignPageTypes,
  CampaignType,
  CookieSet,
  CreativeAssetFromFile,
  CreativeAssetToCreate,
  CustomDomainList,
  FoundCompaniesInfo,
  FoundHubspotPage,
  FoundHubspotPages,
  PublicDomainList,
  RoleTitleGroup,
} from "../../../types/campaignTypes";
import { AdvertiserType } from "../../../types/advertiserTypes";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import {
  SET_ADGROUP_TEMPLATES,
  SET_ADGROUP_TO_CREATE,
  SET_CAMPAIGN_LANDING_PAGE,
  SET_CAMPAIGN_ROLE_TITLE_COOKIES,
  SET_CAMPAIGN_TO_CREATE_BASIC_DETAILS,
  SET_CAMPAIGN_TO_CREATE_SELECTED_ADVERTISER,
  SET_COMPANY_SORT_VALUES,
  SET_CREATED_CUSTOM_DOMAIN_LIST,
  SET_CUSTOM_DOMAIN_LISTS,
  SET_FOUND_COMPANY_INFO,
  SET_FOUND_HUBSPOT_PAGES,
  SET_FROM_FILE,
  SET_GEO_TARGET_OPTIONS,
  SET_IS_PENDING,
  SET_PUBLIC_DOMAIN_LISTS,
  SET_RESET_CAMPAIGN_TO_CREATE,
  SET_ROLE_TITLE_LIST,
  SET_SELECTED_DOMAIN_LISTS,
  SET_SELECTED_GEO_TARGET_OPTIONS,
  SET_SELECTED_SEGMENT_IDS,
  SET_SELECTED_ZIP_CODES,
  SET_UPDATED_CUSTOM_DOMAIN_LISTS,
  SET_USE_ADGROUP_TEMPLATE,
  SetAdGroupToCreate,
  SetCampaignBasicDetails,
  SetCampaignCookies,
  SetCampaignLandingPage,
  SetCompanySortValues,
  SetCreatedCustomDomainList,
  SetCustomDomainLists,
  SetFoundCompanyInfo,
  SetFoundHubspotPages,
  SetFromFile,
  SetGeoTargetOptions,
  SetIsPending,
  SetPublicDomainLists,
  SetResetCampaignToCreate,
  SetRoleTitleList,
  SetSelectedDomainLists,
  SetSelectedGeoTargetOptions,
  SetSelectedSegmentIds,
  SetSelectedZipCodes,
  SetUpdatedCustomDomainLists,
  SetUseAdGroupTemplate,
} from "./types";
import { getCookieStrings } from "../../../utils/getCookieStrings";
import { handleApiResponse } from "../../../utils/handleApiResponse";
import { setApiError } from "../apiError/actions";

export const setIsPending = (isPending: boolean): SetIsPending => {
  return action(SET_IS_PENDING, isPending);
};

export const setResetCampaignToCreate = (): SetResetCampaignToCreate => {
  return action(SET_RESET_CAMPAIGN_TO_CREATE);
};

export const setCampaignBasicDetails = (
  campaignBasicDetails: Partial<CampaignBasicDetailsType>,
): SetCampaignBasicDetails => {
  return action(SET_CAMPAIGN_TO_CREATE_BASIC_DETAILS, campaignBasicDetails);
};

export const setCampaignToCreateSelectedAdvertiser = (payload: AdvertiserType) =>
  action(SET_CAMPAIGN_TO_CREATE_SELECTED_ADVERTISER, payload);

export const setAdGroupTemplates = (payload: AdGroupTemplate[]) => action(SET_ADGROUP_TEMPLATES, payload);

export const setCompanySortValues = (sortValues: any): SetCompanySortValues => {
  return action(SET_COMPANY_SORT_VALUES, sortValues);
};

export const setFoundCompanyInfo = (companyInfo: FoundCompaniesInfo): SetFoundCompanyInfo => {
  return action(SET_FOUND_COMPANY_INFO, companyInfo);
};

export const setFromFile = (fileName: string | null): SetFromFile => {
  return action(SET_FROM_FILE, fileName);
};

export const setFoundHubspostPages = (hubspotPages: FoundHubspotPage[]): SetFoundHubspotPages => {
  return action(SET_FOUND_HUBSPOT_PAGES, hubspotPages);
};

export const setRoleTitlesList = (roleTitleList: RoleTitleGroup[]): SetRoleTitleList => {
  return action(SET_ROLE_TITLE_LIST, roleTitleList);
};

export const setGeoTargetOptions = (geoTargetOptions: Array<any>): SetGeoTargetOptions => {
  return action(SET_GEO_TARGET_OPTIONS, geoTargetOptions);
};

export const setCampaignCookies = (cookieSets: Record<string, CookieSet[]>): SetCampaignCookies => {
  return action(SET_CAMPAIGN_ROLE_TITLE_COOKIES, cookieSets);
};

export const setCampaignLandingPage = (page: string): SetCampaignLandingPage => {
  return action(SET_CAMPAIGN_LANDING_PAGE, page);
};

export const setAdGroupToCreate = (adGroupToCreate: AdGroupToCreateType): SetAdGroupToCreate => {
  return action(SET_ADGROUP_TO_CREATE, adGroupToCreate);
};

export const setAdGroupTemplate = (useAdGroupTemplate: boolean): SetUseAdGroupTemplate => {
  return action(SET_USE_ADGROUP_TEMPLATE, useAdGroupTemplate);
};

export const setSelectedGeoTargetOptions = (selectedGeoTargetOptions: Array<string>): SetSelectedGeoTargetOptions => {
  return action(SET_SELECTED_GEO_TARGET_OPTIONS, selectedGeoTargetOptions);
};

export const setSelectedZipCodes = (selectedZipCodes: Array<string>): SetSelectedZipCodes => {
  return action(SET_SELECTED_ZIP_CODES, selectedZipCodes);
};

export const setSelectedSegmentIds = (selectedSegmentIds: Array<string>): SetSelectedSegmentIds => {
  return action(SET_SELECTED_SEGMENT_IDS, selectedSegmentIds);
};

export const setPublicDomainLists = (publicDomainLists: PublicDomainList[]): SetPublicDomainLists => {
  return action(SET_PUBLIC_DOMAIN_LISTS, publicDomainLists);
};

export const setCustomDomainLists = (customDomainLists: CustomDomainList[]): SetCustomDomainLists => {
  return action(SET_CUSTOM_DOMAIN_LISTS, customDomainLists);
};

export const setCreatedCustomDomainList = (createdCustomDomainList: CustomDomainList): SetCreatedCustomDomainList => {
  return action(SET_CREATED_CUSTOM_DOMAIN_LIST, createdCustomDomainList);
};

export const setUpdatedCustomDomainLists = (
  updatedCustomDomainLists: CustomDomainList[],
): SetUpdatedCustomDomainLists => {
  return action(SET_UPDATED_CUSTOM_DOMAIN_LISTS, updatedCustomDomainLists);
};

export const setSelectedDomainLists = (selectedDomainListsIds: Array<number>): SetSelectedDomainLists => {
  return action(SET_SELECTED_DOMAIN_LISTS, selectedDomainListsIds);
};

export const getRoleTitleList = (): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>): Promise<void> => {
    try {
      dispatch(setIsPending(true));
      const roleTitleList = await api.getRoleTitleList();
      dispatch(setIsPending(false));
      dispatch(handleApiResponse(roleTitleList, setRoleTitlesList, setApiError));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getRoleTitleList: ", error);
      dispatch(setIsPending(false));
    }
  };
};

export const getGeoTargetOptions = (): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>): Promise<void> => {
    try {
      dispatch(setIsPending(true));
      const geoTargetOptions = await api.getGeoTargetOptions();
      dispatch(setIsPending(false));
      dispatch(handleApiResponse(geoTargetOptions, setGeoTargetOptions, setApiError));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getGeoTargetOptions: ", error);
      dispatch(setIsPending(false));
    }
  };
};

export const getAdGroupTemplates = (
  bgAdvertiserId: string,
): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>): Promise<void> => {
    try {
      dispatch(setIsPending(true));
      const adGroupTemplates = await api.getAdGroupTemplatesByAdvertiser(bgAdvertiserId);
      dispatch(setIsPending(false));
      dispatch(handleApiResponse(adGroupTemplates, setAdGroupTemplates, setApiError));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getAdGroupTemplates: ", error);
      dispatch(setIsPending(false));
    }
  };
};

export const getCampaignCompanySortValues =
  (): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> =>
  async (
    dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>,
    getState: () => ReduxApplicationState,
  ): Promise<void> => {
    try {
      dispatch(setIsPending(true));
      const { campaignToCreate } = getState();
      const sortValues = await api.getSortValues(campaignToCreate.selectedAdvertiser!.brandgenAdvertiserId);
      dispatch(setIsPending(false));
      dispatch(handleApiResponse(sortValues, setCompanySortValues, setApiError));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getCampaignCompanySortValues: ", error);
      dispatch(setIsPending(false));
    }
  };

export const getHubspotPages =
  (pageTypes: CampaignPageTypes[]): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> =>
  async (
    dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>,
    getState: () => ReduxApplicationState,
  ): Promise<void> => {
    try {
      dispatch(setIsPending(true));
      const { campaignToCreate } = getState();
      const bgAdvertiserId = campaignToCreate.selectedAdvertiser!.brandgenAdvertiserId;
      const { payload } = await api.getHubspotPages(bgAdvertiserId, pageTypes);

      const fetchedHubspotPages: FoundHubspotPages = payload;

      const blogPages: FoundHubspotPage[] = [];
      const landingPages: FoundHubspotPage[] = [];
      const webPages: FoundHubspotPage[] = [];

      if (fetchedHubspotPages.blogPosts.length !== 0) {
        fetchedHubspotPages.blogPosts.forEach((page: any) => {
          blogPages.push({
            id: page.id,
            name: page.name,
            url: page.absolute_url,
            state: page.state,
            pageType: "BLOG",
          });
        });
      }

      if (fetchedHubspotPages.pages.length !== 0) {
        fetchedHubspotPages.pages.forEach((page: any) => {
          const pageCommonFields = {
            id: page.id,
            name: page.name,
            url: page.absolute_url,
            state: page.state,
          };
          if (page.analytics_page_type === "landing-page") {
            landingPages.push({
              ...pageCommonFields,
              pageType: "LANDING",
            });
          } else {
            webPages.push({
              ...pageCommonFields,
              pageType: "WEB",
            });
          }
        });
      }

      dispatch(setFoundHubspostPages([...landingPages, ...webPages, ...blogPages]));
      dispatch(setIsPending(false));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getHubspotPages: ", error);
      dispatch(setIsPending(false));
    }
  };

export const getCampaignFoundCompaniesInfo =
  (
    queryOpts: Array<any>,
    setInfoIsLoading: (state: boolean) => void,
  ): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> =>
  async (
    dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>,
    getState: () => ReduxApplicationState,
  ): Promise<void> => {
    try {
      const { campaignToCreate } = getState();
      const socket = socketIOClient(process.env.REACT_APP_BACKEND_URL!);

      const data = {
        bgAdvertiserId: campaignToCreate.selectedAdvertiser!.brandgenAdvertiserId,
        filters: queryOpts,
        after: "0",
        numberResults: 100,
        reqID: "test",
      };
      socket.emit("getTargetedCompanies", data);

      socket.on("retrievedCompanies", (companyInfoToDisplay: any) => {
        try {
          setInfoIsLoading(false);
          socket.close();
          dispatch(handleApiResponse(companyInfoToDisplay, setFoundCompanyInfo, setApiError));
        } catch (error) {
          dispatch(setApiError((error as any).toString()));
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getCampaignFoundCompaniesInfo: ", error);
    }
  };

const getCountries = (selectedRegions: Array<string>) => {
  const countries: Array<string> = [];
  if (selectedRegions) {
    selectedRegions.map((region: string) => {
      if (countries.length === 2) {
        return [];
      }

      if (region.includes("USA") && !countries.includes("USA")) {
        countries.push("USA");
      }

      if (region.includes("CAN") && !countries.includes("CAN")) {
        countries.push("CAN");
      }

      // eslint-disable-next-line consistent-return
      return null;
    });

    return countries;
  }

  return [];
};

export const resetCampaignToCreateState =
  (): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> =>
  async (dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>): Promise<void> => {
    dispatch(setResetCampaignToCreate());
  };

export const createAdGroup = async (adGroupToCreate: AdGroupCreationBody) => {
  try {
    const { payload: createdAdGroup } = await api.createAdGroup(adGroupToCreate);
    return createdAdGroup;
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

export const createAdGroupTemplate = async (adGroupTemplateToCreateBody: AdGroupTemplateCreationBody) => {
  try {
    const { payload: createdAdGroupTemplate } = await api.createAdGroupTemplate(adGroupTemplateToCreateBody);
    return createdAdGroupTemplate;
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

export const createCreativeTemplateAsset = async (
  creativeAsset: CreativeAsset,
  file: File,
  adGroupTemplateId: string,
  fileType: string,
  height?: string,
  width?: string,
  duration?: string,
) => {
  const form = new FormData();
  if (fileType === "image") {
    form.append("id", `bg-creative-template-${uuid.v4()}`);
    form.append("adGroupTemplateId", adGroupTemplateId);
    form.append("creativeTemplateSrc", creativeAsset.creativeSrc);
    form.append("creativeType", creativeAsset.creativeType);
    form.append("name", creativeAsset.name);
    form.append("description", creativeAsset.description);
    form.append("height", creativeAsset.height!.toString());
    form.append("width", creativeAsset.width!.toString());
    form.append("file", file);

    await api.createCreativeTemplate(form);
  } else {
    form.append("id", `bg-creative-template-${uuid.v4()}`);
    form.append("adGroupTemplateId", adGroupTemplateId);
    form.append("creativeTemplateSrc", creativeAsset.creativeSrc);
    form.append("creativeType", creativeAsset.creativeType);
    form.append("name", creativeAsset.name);
    form.append("description", creativeAsset.description);
    form.append("height", height ?? creativeAsset.height!.toString());
    form.append("width", width ?? creativeAsset.width!.toString());
    form.append("file", file);
    // form.append("creativeContentBytes", file.size.toString());
    form.append("beeswaxCreativeId", creativeAsset.beeswaxCreativeId.toString());
    form.append("duration", duration ?? "60");

    await api.createCreativeTemplate(form);
  }
};

export const isCreativeAssetFromFile = (asset: CreativeAssetToCreate): asset is CreativeAssetFromFile => {
  return "file" in asset;
};

export const createCreativeAssetsAndTemplates = async (
  creativeAssets: CreativeAssetToCreate[],
  advertiser: AdvertiserType,
  landingPage: string,
  adGroupId: string,
  adGroupTemplateId: string,
  useAdGroupTemplate: boolean,
) => {
  try {
    const createCreative = async (creativeAsset: CreativeAssetToCreate) => {
      const form = new FormData();
      if (creativeAsset.fileType === "image") {
        if (isCreativeAssetFromFile(creativeAsset)) {
          form.append("name", creativeAsset.file.name);
          form.append("file", creativeAsset.file);
          form.append("size", creativeAsset.file.size.toString());
        } else {
          form.append("sourceUrl", creativeAsset.sourceUrl);
          form.append("name", creativeAsset.name);
          form.append("size", creativeAsset.size.toString());
          form.append("mimetype", creativeAsset.mimetype);
        }

        form.append("description", "");
        form.append("height", creativeAsset.height!.toString());
        form.append("width", creativeAsset.width!.toString());
        form.append("advertiser_id", advertiser.beeswaxAdvertiserId!);
        form.append("bg_advertiser_id", advertiser.brandgenAdvertiserId!);
        form.append("bg_creative_id", `bgCreative-${uuid.v4()}`);
        form.append("click_url", landingPage);
        form.append("adGroupId", adGroupId);
        const { payload: createdCreative } = await api.createCreative(form);

        // Create template
        if (!useAdGroupTemplate && createdCreative && adGroupTemplateId && isCreativeAssetFromFile(creativeAsset)) {
          await createCreativeTemplateAsset(
            createdCreative,
            creativeAsset.file,
            adGroupTemplateId,
            creativeAsset.fileType,
          );
        }
      } else {
        if (isCreativeAssetFromFile(creativeAsset)) {
          form.append("name", creativeAsset.file.name);
          form.append("file", creativeAsset.file);
          form.append("size", creativeAsset.file.size.toString());
        } else {
          form.append("sourceUrl", creativeAsset.sourceUrl);
          form.append("name", creativeAsset.name);
          form.append("size", creativeAsset.size.toString());
          form.append("mimetype", creativeAsset.mimetype);
        }

        form.append("description", "");
        form.append("advertiser_id", advertiser.beeswaxAdvertiserId!);
        form.append("bg_advertiser_id", advertiser.brandgenAdvertiserId!);
        form.append("bg_creative_id", `bgCreative-${uuid.v4()}`);
        form.append("click_url", landingPage);
        form.append("height", creativeAsset.height!.toString());
        form.append("width", creativeAsset.width!.toString());
        form.append("adGroupId", adGroupId);
        form.append("duration", creativeAsset.duration?.toString() ?? "60");
        const { payload: createdCreative } = await api.createCreative(form);

        // Create template
        if (!useAdGroupTemplate && createdCreative && adGroupTemplateId && isCreativeAssetFromFile(creativeAsset)) {
          await createCreativeTemplateAsset(
            createdCreative,
            creativeAsset.file,
            adGroupTemplateId,
            creativeAsset.fileType,
            creativeAsset.height!.toString(),
            creativeAsset.width!.toString(),
            creativeAsset.duration?.toString(),
          );
        }
      }
    };
    await Promise.all(creativeAssets.map((creativeAsset) => createCreative(creativeAsset)));
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

const getCreativesByAdGroupId = async (adGroupId: string) => {
  try {
    const { payload: adGroupCreatives } = await api.getCreativesByAdGroup(adGroupId);
    return adGroupCreatives;
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

export const createCampaign = (
  onCampaignCreated: (createdCampaign: CampaignType) => void,
): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> => {
  return async (
    dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>,
    getState: () => ReduxApplicationState,
  ): Promise<void> => {
    try {
      dispatch(setIsPending(true));

      const state = getState();
      const { user, campaignToCreate } = state;
      const { basicDetails, selectedAdvertiser } = campaignToCreate;
      const { agencyId } = basicDetails;
      const socket = socketIOClient(process.env.REACT_APP_BACKEND_URL!);

      // Campaign id generation
      const brandgenCampaignId = `bg-campaign-${uuid.v4()}`;

      // Create Ad Group
      const adGroupToCreateBody = {
        adGroupId: `bg-ad-group-${uuid.v4()}`,
        brandgenAdvertiserId: campaignToCreate.selectedAdvertiser?.brandgenAdvertiserId!,
        brandgenCampaignId,
        createdBy: user.currentUser,
        name: campaignToCreate.adGroupToCreate.name,
        description: campaignToCreate.adGroupToCreate.description ?? "",
        adGroupType: campaignToCreate.adGroupToCreate.adGroupType,
      };
      const createdAdGroup: AdGroup = await createAdGroup(adGroupToCreateBody);

      // Create Ad Group template
      let createdAdGroupTemplate: AdGroupTemplate = {
        id: "",
        brandgenAdvertiserId: "",
        createdBy: "",
        name: "",
        description: "",
        adGroupType: AdGroupType.IMAGE,
        createdAt: "",
        updatedAt: "",
      };
      if (!campaignToCreate.useAdGroupTemplate && createdAdGroup) {
        const adGroupTemplateToCreateBody: AdGroupTemplateCreationBody = {
          id: `bg-ad-group-template-${uuid.v4()}`,
          brandgenAdvertiserId: createdAdGroup.brandgenAdvertiserId!,
          createdBy: createdAdGroup.createdBy,
          name: createdAdGroup.name,
          description: createdAdGroup.description,
          adGroupType: createdAdGroup.adGroupType,
        };
        createdAdGroupTemplate = await createAdGroupTemplate(adGroupTemplateToCreateBody);
      }

      // Create Creatives and tempaltes
      await createCreativeAssetsAndTemplates(
        campaignToCreate.adGroupToCreate.creativeAssets,
        selectedAdvertiser!,
        campaignToCreate.landingPage,
        createdAdGroup.adGroupId,
        createdAdGroupTemplate.id,
        campaignToCreate.useAdGroupTemplate,
      );
      const createdCreatives: CreativeAssetsByAdGroup = await getCreativesByAdGroupId(createdAdGroup.adGroupId);

      // Create campaign
      const body = {
        brandgenCampaignId,
        agencyId,
        beeswaxCampaign: {
          active: false,
          advertiser_id: Number.parseInt(selectedAdvertiser!.beeswaxAdvertiserId),
          budget_type: 2,
          campaign_budget: basicDetails.estimatedBudget,
          dailyBudget: basicDetails.dailyBudget,
          campaign_name: basicDetails.name,
          start_date:
            typeof basicDetails.startDate === "string"
              ? new Date(Date.parse(basicDetails.startDate))
              : basicDetails.startDate,
          end_date:
            typeof basicDetails.endDate === "string"
              ? new Date(Date.parse(basicDetails.endDate))
              : basicDetails.endDate,
        },
        fromFile: campaignToCreate.fromFile,
        max_bid: basicDetails.maxBid,
        brandgenAdvertiserId: selectedAdvertiser!.brandgenAdvertiserId,
        createdBy: basicDetails.createdBy,
        creativeIds: createdCreatives.creatives.map(
          (creative: CreativeAsset) => creative.beeswaxCreativeId,
        ) as number[],
        description: basicDetails.description,
        name: basicDetails.name,
        targetingInfo: {
          domain_list: campaignToCreate.selectedDomainListsIds,
          geo_country: getCountries(campaignToCreate.selectedGeoTargetOptions),
          geo_state: campaignToCreate.selectedGeoTargetOptions,
          geo_zip: campaignToCreate.selectedZipCodes,
          role_title_segments: getCookieStrings(campaignToCreate.campaignCookieSets as Record<string, CookieSet[]>),
          retargeting_segments: campaignToCreate.selectedSegmentIds,
        },
        lineItems: campaignToCreate.foundCompanyInfo?.companies,
        matches: campaignToCreate.foundCompanyInfo?.matches,
        isVideoCampaign: basicDetails.isVideoCampaign,
        showInHubspot: basicDetails.showInHubspot,
        maxCompanies: basicDetails.maxCompanies,
        isCustomCampaign: !!window.location.href.includes("create/custom"),
        campaignType: localStorage.getItem("customCampaignType"),
      };

      localStorage.removeItem("customCampaignType");

      socket.emit("createCampaign", body);

      socket.on("campaignCreated", (response: APIResponse) => {
        socket.close();
        dispatch(setIsPending(false));
        onCampaignCreated(response.payload);
      });

      socket.on("campaignCreationError", (error: any) => {
        // eslint-disable-next-line no-console
        console.log("createCampaign: ", error);
        dispatch(setIsPending(false));
        socket.close();
      });
      dispatch(setIsPending(true));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("createCampaign: ", error);
      dispatch(setIsPending(false));
    }
  };
};
